import React, { useState, useEffect } from 'react';
import './MonthlyJournalEntries.css';
import { apiCall } from '../config';

const MonthlyJournalEntriesDepletion = () => {
  const [fgDepletionData, setFgDepletionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthsLoaded, setMonthsLoaded] = useState(false);
  const [qboAccountMapping, setQboAccountMapping] = useState([]);
  const [journalPreview, setJournalPreview] = useState([]);
  const [isLoadingJournal, setIsLoadingJournal] = useState(false);
  const [isSubmittingToQBO, setIsSubmittingToQBO] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [journalError, setJournalError] = useState(null);
  
  // New state for COGS calculations
  const [cogsData, setCogsData] = useState([]);
  const [isLoadingCogs, setIsLoadingCogs] = useState(false);
  const [cogsError, setCogsError] = useState(null);

  // New state for Bundle depletion data
  const [bundleDepletionData, setBundleDepletionData] = useState([]);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [bundleError, setBundleError] = useState(null);
  const [totalBundleAmount, setTotalBundleAmount] = useState(0);
  const [totalBundles, setTotalBundles] = useState(0);

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    if (availableMonths.length > 0 && selectedMonth && monthsLoaded) {
      fetchFgDepletionData();
      fetchCogsData();
      fetchBundleDepletionData(); // Fetch bundle data when month changes
    }
  }, [selectedMonth, availableMonths, monthsLoaded]);

  // Fetch QBO account mapping on mount
  useEffect(() => {
    const fetchQboMapping = async () => {
      try {
        const response = await apiCall('/api/monthly-journal-entries-depletion/qbo-account-mapping', { method: 'GET' });
        if (response.ok) {
          const data = await response.json();
          setQboAccountMapping(data);
        }
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchQboMapping();
  }, []);

  // Generate journal preview when FG data or mapping changes
  useEffect(() => {
    if (!fgDepletionData.length || !qboAccountMapping.length) {
      setJournalPreview([]);
      return;
    }
    
    // Fetch journal preview from backend
    const fetchJournalPreview = async () => {
      try {
        setIsLoadingJournal(true);
        setJournalError(null);
        
        const url = selectedMonth 
          ? `/api/monthly-journal-entries-depletion/journal-preview?month=${selectedMonth}`
          : '/api/monthly-journal-entries-depletion/journal-preview';
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Transform the data to match the expected format
          const rows = data.journal_rows.map(row => ({
            row_number: row.row_number,
            account: `${row.account_name} (${row.account_id})`,
            account_id: row.account_id,
            description: row.description,
            type: row.type,
            amount: row.amount
          }));
          
          setJournalPreview(rows);
        } else {
          setJournalError(data.error || 'Failed to generate journal preview');
        }
      } catch (err) {
        setJournalError('Network error: ' + err.message);
      } finally {
        setIsLoadingJournal(false);
      }
    };
    
    fetchJournalPreview();
  }, [fgDepletionData, qboAccountMapping, selectedMonth]);

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch('/api/monthly-journal-entries-depletion/available-months', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setAvailableMonths(data.months);
        
        // Set the most recent COMPLETED month as default
        if (data.months.length > 0) {
          // Get current date
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
          
          // The most recent COMPLETED month is always the previous month
          // (since we can only send journal entries for completed months)
          let targetYear = currentYear;
          let targetMonth = currentMonth - 1;
          
          // Handle year transition (January -> December of previous year)
          if (targetMonth === 0) {
            targetMonth = 12;
            targetYear = currentYear - 1;
          }
          
          // Format the target month as YYYY-MM-DD (first day of month)
          const targetMonthStr = `${targetYear}-${targetMonth.toString().padStart(2, '0')}-01`;
          
          // Find the closest available month (either exact match or most recent)
          let defaultMonth = data.months[0].value; // fallback to first available
          
          // First try to find exact match
          const exactMatch = data.months.find(month => month.value === targetMonthStr);
          if (exactMatch) {
            defaultMonth = exactMatch.value;
          } else {
            // Find the most recent month that's not in the future
            for (const month of data.months) {
              if (month.value <= targetMonthStr) {
                defaultMonth = month.value;
              } else {
                break; // Stop when we hit a future month
              }
            }
          }
          
          setSelectedMonth(defaultMonth);
        }
        setMonthsLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching available months:', err);
    }
  };

  const fetchFgDepletionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = selectedMonth 
        ? `/api/monthly-journal-entries-depletion/fg-depletion-data?month=${selectedMonth}`
        : '/api/monthly-journal-entries-depletion/fg-depletion-data';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setFgDepletionData(data.data);
        setTotalAmount(data.total_amount);
        setTotalItems(data.total_items);
      } else {
        setError(data.error || 'Failed to fetch FG depletion data');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCogsData = async () => {
    try {
      setIsLoadingCogs(true);
      setCogsError(null);
      
      const url = selectedMonth 
        ? `/api/monthly-journal-entries-depletion/cogs-data?month=${selectedMonth}`
        : '/api/monthly-journal-entries-depletion/cogs-data';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setCogsData(data.data);
      } else {
        setCogsError(data.error || 'Failed to fetch COGS data');
      }
    } catch (err) {
      setCogsError('Network error: ' + err.message);
    } finally {
      setIsLoadingCogs(false);
    }
  };

  const fetchBundleDepletionData = async () => {
    try {
      setIsLoadingBundles(true);
      setBundleError(null);
      
      const url = selectedMonth 
        ? `/api/monthly-journal-entries-depletion/bundle-depletion-data?month=${selectedMonth}`
        : '/api/monthly-journal-entries-depletion/bundle-depletion-data';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setBundleDepletionData(data.data);
        setTotalBundleAmount(data.total_amount);
        setTotalBundles(data.total_items);
      } else {
        setBundleError(data.error || 'Failed to fetch Bundle depletion data');
      }
    } catch (err) {
      setBundleError('Network error: ' + err.message);
    } finally {
      setIsLoadingBundles(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const submitToQBO = async () => {
    try {
      setIsSubmittingToQBO(true);
      setJournalError(null);
      setSuccessMessage(null);
      
      const payload = {
        journal_entry: journalPreview.map(row => ({
          account_id: row.account_id,
          description: row.description,
          type: row.type,
          amount: row.amount
        })),
        month: selectedMonth
      };
      
      const response = await fetch('/api/monthly-journal-entries-depletion/submit-to-qbo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'Journal entry posted to QBO successfully!');
        alert(`✅ ${data.message || 'Journal entry posted to QBO successfully!'}\n\nQBO ID: ${data.qbo_id}\nTransaction Date: ${data.transaction_date}\nLines: ${data.lines_count}\nTotal Amount: ${formatCurrency(data.total_amount)}`);
      } else {
        const errorData = await response.json();
        setJournalError(errorData.error || 'Failed to submit journal entry to QBO');
        alert(`❌ ${errorData.error || 'Failed to submit journal entry to QBO'}`);
      }
    } catch (err) {
      setJournalError('Network error occurred while submitting to QBO');
      alert('❌ Network error occurred while submitting to QBO');
    } finally {
      setIsSubmittingToQBO(false);
    }
  };

  if (loading || !monthsLoaded) {
    return (
      <div className="sundeep-invoice-process">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{!monthsLoaded ? 'Loading available months...' : 'Loading FG depletion data...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="monthly-journal-entries">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchFgDepletionData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sundeep-invoice-process">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Monthly JE - Finished Goods Depletion</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Calculate and prepare journal entries for QBO export</li>
              <li>Filter by month to see specific period data</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>
      <div className="page-content">
        {/* Month Filter - Moved to top */}
        <div className="section">
          <div className="filter-controls">
            <div className="month-filter">
              <label htmlFor="month-select">Select Month:</label>
              <select 
                id="month-select"
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={loading}
              >
                <option value="">All Months</option>
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Journal Entry Preview Section - Moved to top */}
        <div className="section">
          <h2>📝 Journal Entry Preview - Finished Goods Depletion</h2>
          {isLoadingJournal ? (
            <div className="loading">Loading journal entry preview...</div>
          ) : journalError ? (
            <div className="error">❌ {journalError}</div>
          ) : successMessage ? (
            <div className="success">✅ {successMessage}</div>
          ) : journalPreview.length > 0 ? (
            <div className="journal-entry-container">
              <div className="journal-entry-summary">
                <h3>Journal Entry Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item">
                    <strong>Total Debits:</strong> {formatCurrency(journalPreview.filter(row => row.type === 'DEBIT').reduce((sum, row) => sum + row.amount, 0))}
                  </div>
                  <div className="stat-item">
                    <strong>Total Credits:</strong> {formatCurrency(journalPreview.filter(row => row.type === 'CREDIT').reduce((sum, row) => sum + row.amount, 0))}
                  </div>
                  <div className="stat-item">
                    <strong>Rows:</strong> {journalPreview.length}
                  </div>
                </div>
              </div>
              <table className="journal-entry-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Account</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {journalPreview.map((row) => (
                    <tr key={row.row_number} className={`journal-row ${row.type.toLowerCase()}`}> 
                      <td className="row-number">{row.row_number}</td>
                      <td className="account">{row.account}</td>
                      <td className="description">{row.description}</td>
                      <td className="type">
                        <span className={`type-badge ${row.type.toLowerCase()}`}>{row.type.toUpperCase()}</span>
                      </td>
                      <td className="amount">{formatCurrency(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="journal-entry-actions">
                <button 
                  className="btn btn-primary"
                  onClick={submitToQBO}
                  disabled={isSubmittingToQBO || journalPreview.length === 0}
                >
                  {isSubmittingToQBO ? '⏳ Submitting...' : '📤 Submit to QuickBooks Online'}
                </button>
              </div>
            </div>
          ) : (
            <div className="no-data">No journal entry data available for this month.</div>
          )}
        </div>

        <div className="section">
          <h2>Finished Goods Inventory Depletion</h2>
          <p>Inventory to be depleted based on sales data from Amazon, Shopify, and Trade</p>
          
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Items:</span>
              <span className="stat-value">{formatNumber(totalItems)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Amount:</span>
              <span className="stat-value">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
          
          {fgDepletionData.length === 0 ? (
            <div className="no-data">
              <p>No FG depletion data found. Please check if sales data has been imported.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>IGD SKU Number</th>
                    <th>IGD Name</th>
                    <th>Amazon Sales Qty</th>
                    <th>Shopify Sales Qty</th>
                    <th>Trade Sales Qty</th>
                    <th>Total Quantity</th>
                    <th>Cost per Unit</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {fgDepletionData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.igd_sku_number}</td>
                      <td>{item.igd_name}</td>
                      <td>{formatNumber(item.amazon_quantity)}</td>
                      <td>{formatNumber(item.shopify_quantity)}</td>
                      <td>{formatNumber(item.trade_quantity)}</td>
                      <td>{formatNumber(item.total_quantity)}</td>
                      <td>{formatCurrency(item.cost_per_unit)}</td>
                      <td className="amount-cell">{formatCurrency(item.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* COGS Calculations Section */}
        <div className="section">
          <h2>💰 COGS Calculations</h2>
          <p>Cost of Goods Sold calculations based on sales quantities and BOM structure</p>
          
          {isLoadingCogs ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading COGS calculations...</p>
            </div>
          ) : cogsError ? (
            <div className="error-container">
              <h3>Error</h3>
              <p>{cogsError}</p>
              <button onClick={fetchCogsData} className="retry-button">
                Retry
              </button>
            </div>
          ) : cogsData.length === 0 ? (
            <div className="no-data">
              <p>No COGS data found. Please check if BOM structure and component costs are configured.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>IGD SKU Number</th>
                    <th>IGD Name</th>
                    <th>Total Quantity</th>
                    <th>5100 Cost per Unit</th>
                    <th>5100 Total</th>
                    <th>5200 Cost per Unit</th>
                    <th>5200 Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cogsData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.igd_sku_number}</td>
                      <td>{item.igd_name}</td>
                      <td>{formatNumber(item.total_quantity)}</td>
                      <td>{formatCurrency(item.cost_5100_per_unit)}</td>
                      <td className="amount-cell">{formatCurrency(item.cost_5100_total)}</td>
                      <td>{formatCurrency(item.cost_5200_per_unit)}</td>
                      <td className="amount-cell">{formatCurrency(item.cost_5200_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bundle Depletion Section */}
        <div className="section">
          <h2>🎁 Bundle Depletion</h2>
          <p>Bundle inventory to be depleted based on sales data</p>
          
          {isLoadingBundles ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading Bundle depletion data...</p>
            </div>
          ) : bundleError ? (
            <div className="error-container">
              <h3>Error</h3>
              <p>{bundleError}</p>
              <button onClick={fetchBundleDepletionData} className="retry-button">
                Retry
              </button>
            </div>
          ) : bundleDepletionData.length === 0 ? (
            <div className="no-data">
              <p>No Bundle depletion data found. Please check if sales data has been imported.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bundle SKU</th>
                    <th>Bundle Name</th>
                    <th>Component SKU</th>
                    <th>Component Name</th>
                    <th>BOM Qty</th>
                    <th>Bundle Qty Sold</th>
                    <th>Component Qty Consumed</th>
                    <th>Cost per Unit</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {bundleDepletionData.map((bundle, bundleIndex) => (
                    <React.Fragment key={bundleIndex}>
                      {/* Bundle header row */}
                      <tr className="bundle-header">
                        <td colSpan="9" className="bundle-title">
                          <strong>📦 {bundle.bundle_name} ({bundle.bundle_sku})</strong>
                          <span className="bundle-total">Total: {formatCurrency(bundle.bundle_total_amount)}</span>
                        </td>
                      </tr>
                      {/* Component rows */}
                      {bundle.components.map((component, compIndex) => (
                        <tr key={`${bundleIndex}-${compIndex}`} className="component-row">
                          <td></td>
                          <td></td>
                          <td>{component.component_sku}</td>
                          <td>{component.component_name}</td>
                          <td>{formatNumber(component.bom_quantity)}</td>
                          <td>{formatNumber(component.bundle_quantity_sold)}</td>
                          <td>{formatNumber(component.component_quantity_consumed)}</td>
                          <td>{formatCurrency(component.cost_per_unit)}</td>
                          <td className="amount-cell">{formatCurrency(component.total_amount)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="actions">
          <button 
            onClick={fetchFgDepletionData} 
            className="refresh-button"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyJournalEntriesDepletion; 